export type TextSignalSeverity = "info" | "monitorar" | "cautela" | "relevante" | "urgente";
export type TextSignalConfidence = "baixa" | "moderada" | "alta";

export interface ClinicalTextSource {
  id: string;
  sourceType: "paciente" | "sessão" | "check-in" | "dose";
  field: string;
  label: string;
  text: string;
  at?: string | null;
}

export interface ClinicalTextEvidence {
  sourceId: string;
  sourceLabel: string;
  field: string;
  excerpt: string;
  negated: boolean;
}

export interface ClinicalTextSignal {
  id: string;
  label: string;
  severity: TextSignalSeverity;
  confidence: TextSignalConfidence;
  hypothesis: string;
  alternativeExplanations: string[];
  management: string;
  validation: string[];
  positiveEvidence: ClinicalTextEvidence[];
  negatedEvidence: ClinicalTextEvidence[];
  contradiction: boolean;
}

export interface ClinicalTextInterpretation {
  sourcesRead: ClinicalTextSource[];
  signals: ClinicalTextSignal[];
  sourceCounts: Record<string, number>;
  textCoverageScore: number;
  limitations: string[];
}

interface ThemeRule {
  id: string;
  label: string;
  severity: TextSignalSeverity;
  patterns: RegExp[];
  hypothesis: string;
  alternatives: string[];
  management: string;
  validation: string[];
}

function normalized(value: unknown) {
  return String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function safeText(value: unknown) {
  const text = String(value ?? "").trim();
  return text.length >= 3 ? text : "";
}

function pushSource(
  sources: ClinicalTextSource[],
  sourceType: ClinicalTextSource["sourceType"],
  ownerId: string,
  field: string,
  label: string,
  value: unknown,
  at?: string | null,
) {
  const text = safeText(value);
  if (!text) return;
  sources.push({ id: `${sourceType}-${ownerId}-${field}`, sourceType, field, label, text, at });
}

export function collectClinicalTextSources(input: {
  patient?: any;
  sessions?: any[];
  checkins?: any[];
  doses?: any[];
}): ClinicalTextSource[] {
  const sources: ClinicalTextSource[] = [];
  const patient = input.patient ?? {};
  const patientId = patient.id ?? "current";
  pushSource(sources, "paciente", patientId, "current_complaint", "Queixa atual", patient.current_complaint);
  pushSource(sources, "paciente", patientId, "clinical_history", "História clínica", patient.clinical_history);
  pushSource(sources, "paciente", patientId, "substance_use_history", "História de substâncias", patient.substance_use_history);
  pushSource(sources, "paciente", patientId, "baseline_state", "Estado basal", patient.baseline_state);
  pushSource(sources, "paciente", patientId, "notes", "Notas do paciente", patient.notes);

  for (const session of input.sessions ?? []) {
    const id = session.id ?? String(session.session_at ?? session.created_at ?? Math.random());
    const at = session.session_at ?? session.created_at ?? null;
    pushSource(sources, "sessão", id, "complaint", "Sessão · queixa", session.complaint, at);
    pushSource(sources, "sessão", id, "baseline_state", "Sessão · baseline", session.baseline_state, at);
    pushSource(sources, "sessão", id, "patient_narrative", "Sessão · narrativa", session.patient_narrative, at);
    pushSource(sources, "sessão", id, "physician_observation", "Sessão · observação médica", session.physician_observation, at);
    pushSource(sources, "sessão", id, "clinical_summary", "Sessão · resumo clínico", session.clinical_summary, at);
    pushSource(sources, "sessão", id, "mental_status_exam", "Sessão · exame do estado mental", session.mental_status_exam, at);
    pushSource(sources, "sessão", id, "assessment", "Sessão · avaliação", session.assessment, at);
    pushSource(sources, "sessão", id, "plan", "Sessão · plano", session.plan, at);
  }

  for (const checkin of input.checkins ?? []) {
    const id = checkin.id ?? String(checkin.checkin_at ?? checkin.created_at ?? Math.random());
    const at = checkin.checkin_at ?? checkin.created_at ?? null;
    pushSource(sources, "check-in", id, "patient_report", "Check-in · relato", checkin.patient_report, at);
    pushSource(sources, "check-in", id, "physician_observation", "Check-in · observação médica", checkin.physician_observation, at);
    pushSource(sources, "check-in", id, "adverse_event", "Check-in · evento adverso", checkin.adverse_event, at);
    pushSource(sources, "check-in", id, "trigger_text", "Check-in · gatilho", checkin.trigger_text, at);
    pushSource(sources, "check-in", id, "context_text", "Check-in · contexto", checkin.context_text, at);
  }

  for (const dose of input.doses ?? []) {
    const id = dose.id ?? String(dose.actual_time ?? Math.random());
    const at = dose.actual_time ?? dose.created_at ?? null;
    pushSource(sources, "dose", id, "perceived_effect_text", `Dose · efeito percebido (${dose.substance_name ?? dose.substances?.name ?? "substância"})`, dose.perceived_effect_text, at);
    pushSource(sources, "dose", id, "adverse_effects", `Dose · efeitos adversos (${dose.substance_name ?? dose.substances?.name ?? "substância"})`, Array.isArray(dose.adverse_effects) ? dose.adverse_effects.join("; ") : dose.adverse_effects, at);
    pushSource(sources, "dose", id, "notes_patient", "Dose · nota do paciente", dose.notes_patient, at);
    pushSource(sources, "dose", id, "notes_clinician", "Dose · nota clínica", dose.notes_clinician, at);
    pushSource(sources, "dose", id, "context_notes", "Dose · contexto", dose.context_notes, at);
  }

  return sources;
}

const THEMES: ThemeRule[] = [
  {
    id: "suicide-risk",
    label: "Ideação suicida ou desesperança grave",
    severity: "urgente",
    patterns: [/ideacao suicida|pensamento suicida|quer morrer|nao quer viver|sem vontade de viver|se matar|suicidio|despedida|sem saida/gi],
    hypothesis: "O texto contém sinal que exige avaliação clínica direta de risco, intenção, plano, meios, proteção e mudança recente.",
    alternatives: ["expressão figurativa", "histórico remoto", "negação explícita", "relato sobre terceiro"],
    management: "Realizar avaliação estruturada de risco e documentar temporalidade, intenção, planejamento, acesso a meios e fatores protetores. Não delegar decisão de segurança ao algoritmo.",
    validation: ["é atual ou histórico?", "há intenção/plano/meios?", "houve mudança recente?", "quais fatores protetores estão ativos?"],
  },
  {
    id: "psychosis-activation",
    label: "Ativação, mania ou fenômeno psicótico",
    severity: "relevante",
    patterns: [/paranoi|persegu|ideias? de referencia|ouvindo vozes|alucin|deliri|grandios|acelerad[oa]|sem dormir e nao sente sono|muito mais energia|pensamento rapido|fala pressionada|desconfianca extrema/gi],
    hypothesis: "Há linguagem compatível com ativação, alteração de realidade ou hiperalerta grave. Diferenciar fenômeno basal, privação de sono, substância, efeito farmacológico e transtorno primário.",
    alternatives: ["ansiedade/hipervigilância", "TEPT", "privação de sono", "intoxicação/abstinência", "efeito adverso medicamentoso"],
    management: "Localizar o fenômeno no tempo, cruzar com sono, cafeína, redose, substâncias e fase PK/PD; priorizar segurança e exame do estado mental.",
    validation: ["convicção e crítica", "percepções anômalas", "sono nas 72h", "relação temporal com doses/substâncias"],
  },
  {
    id: "anhedonia-low-drive",
    label: "Anedonia, apatia ou baixa ativação",
    severity: "relevante",
    patterns: [/anedoni|sem prazer|nada tem graca|sem perspectiva|sem motivacao|nao consegue sentir|apatia|vazio|sem energia|falta de vontade|nao ve sentido/gi],
    hypothesis: "O texto sugere anedonia/baixa ativação funcional. A origem pode ser depressiva, pós-substância, relacionada ao sono, sedação, trauma ou disfunção executiva.",
    alternatives: ["privação de sono", "sedação/ressaca medicamentosa", "abstinência ou neuroadaptação", "burnout", "evitamento ansioso"],
    management: "Separar prazer, antecipação, motivação, energia e iniciação de tarefas; acompanhar por horário e contexto antes de atribuir a uma única medicação.",
    validation: ["prazer consumatório vs antecipatório", "energia", "iniciação", "variação diurna", "relação com sono e substâncias"],
  },
  {
    id: "executive-dysfunction",
    label: "Disfunção executiva e iniciação",
    severity: "monitorar",
    patterns: [/nao consigo iniciar|nao consigo comecar|travado|procrastin|desorganiz|nao termina|nao conclui|perde o foco|desatenc|hiperfoco|funcao executiva|memoria operacional/gi],
    hypothesis: "Há sinal de disfunção executiva. Diferenciar déficit basal, ansiedade, baixa ativação, sono ruim, sobrecarga, sedação e queda/offset farmacológico.",
    alternatives: ["ansiedade de desempenho", "anedonia", "privação de sono", "sedação residual", "sobrecarga ambiental"],
    management: "Registrar tarefa concreta, horário, fase da curva, esforço para iniciar, sustentação e resultado funcional; não usar apenas sensação subjetiva de produtividade.",
    validation: ["iniciação", "sustentação", "conclusão", "qualidade do resultado", "fase PK/PD"],
  },
  {
    id: "anxiety-autonomic",
    label: "Ansiedade, hiperalerta ou ativação autonômica",
    severity: "cautela",
    patterns: [/ansiedad|hiperalerta|panico|tremor|palpit|taquic|coracao acelerado|sudorese|tensao|agitado|inquiet|falta de ar|aperto no peito/gi],
    hypothesis: "O texto sugere ansiedade e/ou ativação autonômica. É necessário distinguir eixo afetivo basal de come-up/pico/offset, cafeína, privação de sono ou interação.",
    alternatives: ["efeito farmacológico", "cafeína", "privação de sono", "condição cardiovascular", "pânico/TEPT"],
    management: "Cruzar horário dos sintomas com dose, cafeína, alimento, sono, FC/PA e contexto emocional; documentar intensidade e resolução.",
    validation: ["horário de início", "FC/PA", "cafeína", "fase da curva", "gatilho emocional"],
  },
  {
    id: "sleep-disturbance",
    label: "Insônia, pesadelos ou sono não restaurador",
    severity: "cautela",
    patterns: [/insoni|nao dorme|nao conseguiu dormir|pesadel|desperta|sono ruim|sono leve|acorda cansado|ressaca matinal|dormiu pouco|latencia do sono/gi],
    hypothesis: "Há alteração de sono com potencial para contaminar a interpretação de humor, foco, ansiedade, psicose e resposta farmacológica.",
    alternatives: ["estimulante/cafeína", "hiperalerta traumático", "sedativo com ressaca", "ritmo circadiano", "substância/abstinência"],
    management: "Quantificar duração, latência, despertares, qualidade e relação com doses; marcar dias privados de sono como menor confiabilidade para avaliar estimulantes.",
    validation: ["horas totais", "latência", "despertares", "horário de doses", "cafeína e substâncias"],
  },
  {
    id: "sedation-cognitive-cost",
    label: "Sedação ou lentificação cognitiva",
    severity: "cautela",
    patterns: [/sedad|sonolenc|muito sono|lentific|ressaca|grogue|cabeca pesada|confus|memoria ruim|fala pastosa|dificuldade de acordar/gi],
    hypothesis: "O texto sugere sedação/ressaca com possível custo executivo e risco de compensação por estimulante ou cafeína.",
    alternatives: ["privação de sono", "depressão", "condição médica", "efeito residual de substância"],
    management: "Separar benefício noturno de custo diurno; cruzar horário de sedativos, sono, desempenho matinal, quedas, memória e compensações.",
    validation: ["horário e dose sedativa", "tempo até despertar funcional", "coordenação/memória", "cafeína/estimulante compensatório"],
  },
  {
    id: "redose-craving",
    label: "Redose, fissura ou uso por restauração funcional",
    severity: "relevante",
    patterns: [/redos|dose extra|tomou de novo|queria sentir produtiv|precisava funcionar|fissura|craving|vontade de usar|compuls|nao aguentou a queda|busca de efeito|uso problematico/gi],
    hypothesis: "Há fenômeno de redose/fissura ou automedicação funcional. Diferenciar reforço negativo, busca hedônica, ansiedade de desempenho, queda da curva e tolerância subjetiva.",
    alternatives: ["erro de registro", "fracionamento prescrito", "dose SOS autorizada", "uso para evitar abstinência"],
    management: "Registrar motivo antes da dose, fase da curva, expectativa, craving, efeito e custo 1–3h depois; não moralizar nem assumir abuso automaticamente.",
    validation: ["intenção da dose", "foi prescrita/fracionada?", "fase da curva", "craving antes/depois", "consequência funcional"],
  },
  {
    id: "tolerance-loss-of-effect",
    label: "Perda de efeito ou tolerância percebida",
    severity: "monitorar",
    patterns: [/nao faz mais efeito|parou de funcionar|efeito diminuiu|precisa de mais|tolerancia|nao bate|dura menos|acabando mais cedo|perdeu o efeito/gi],
    hypothesis: "O texto relata perda de efeito percebida. Isso pode representar tolerância, contexto desfavorável, expectativa hedônica, erro de formulação/horário, sono ruim ou progressão do quadro.",
    alternatives: ["sono/cafeína", "formulação diferente", "adesão irregular", "sedação concomitante", "anedonia", "mudança de expectativa"],
    management: "Comparar medidas funcionais em dias equivalentes e curvas por formulação; exigir trajetória repetida antes de inferir tolerância farmacológica.",
    validation: ["qual efeito diminuiu?", "em qual fase/horário?", "formulação e adesão", "sono/cafeína", "benefício objetivo vs sensação de impacto"],
  },
  {
    id: "depressed-mood",
    label: "Humor deprimido, culpa ou desesperança",
    severity: "relevante",
    patterns: [/deprimid|triste|culpa|ruina|fracass|desesperanc|inutil|sem valor|choro|humor baixo/gi],
    hypothesis: "O texto sugere humor deprimido e cognições negativas. Avaliar gravidade, duração, reatividade, risco e relação com trauma, substâncias, sono e curso longitudinal.",
    alternatives: ["reação situacional", "TEPT", "abstinência", "efeito de sedação", "burnout"],
    management: "Quantificar trajetória, funcionamento, risco e sintomas vegetativos; cruzar com anedonia, sono e substâncias.",
    validation: ["duração", "reatividade", "funcionamento", "sintomas vegetativos", "risco suicida"],
  },
];

function isNegated(text: string, index: number) {
  const before = normalized(text.slice(Math.max(0, index - 80), index));
  return /(?:^|\b)(?:sem|nega|negou|nao|ausencia de|descarta|sem evidencia de)(?:\s+\w+){0,6}\s*$/.test(before);
}

function excerpt(text: string, index: number, length: number) {
  const start = Math.max(0, index - 55);
  const end = Math.min(text.length, index + length + 90);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < text.length ? "…" : "";
  return `${prefix}${text.slice(start, end).replace(/\s+/g, " ").trim()}${suffix}`;
}

function confidence(positive: ClinicalTextEvidence[]): TextSignalConfidence {
  const sourceTypes = new Set(positive.map((evidence) => evidence.sourceLabel.split(" · ")[0]));
  if (positive.length >= 4 && sourceTypes.size >= 2) return "alta";
  if (positive.length >= 2 || sourceTypes.size >= 2) return "moderada";
  return "baixa";
}

export function interpretClinicalTexts(input: {
  patient?: any;
  sessions?: any[];
  checkins?: any[];
  doses?: any[];
}): ClinicalTextInterpretation {
  const sources = collectClinicalTextSources(input);
  const signals: ClinicalTextSignal[] = [];

  for (const theme of THEMES) {
    const positiveEvidence: ClinicalTextEvidence[] = [];
    const negatedEvidence: ClinicalTextEvidence[] = [];
    const seen = new Set<string>();

    for (const source of sources) {
      const searchableText = normalized(source.text);
      for (const pattern of theme.patterns) {
        pattern.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = pattern.exec(searchableText)) !== null) {
          const key = `${source.id}-${match.index}-${match[0]}`;
          if (seen.has(key)) continue;
          seen.add(key);
          const evidence: ClinicalTextEvidence = {
            sourceId: source.id,
            sourceLabel: source.label,
            field: source.field,
            excerpt: excerpt(source.text, match.index, match[0].length),
            negated: isNegated(source.text, match.index),
          };
          if (evidence.negated) negatedEvidence.push(evidence);
          else positiveEvidence.push(evidence);
          if (match[0].length === 0) pattern.lastIndex += 1;
        }
      }
    }

    if (!positiveEvidence.length && !negatedEvidence.length) continue;
    signals.push({
      id: theme.id,
      label: theme.label,
      severity: positiveEvidence.length ? theme.severity : "info",
      confidence: positiveEvidence.length ? confidence(positiveEvidence) : "baixa",
      hypothesis: positiveEvidence.length ? theme.hypothesis : `Há apenas menção negada/ausente de ${theme.label.toLowerCase()}; não tratar como achado positivo.`,
      alternativeExplanations: theme.alternatives,
      management: theme.management,
      validation: theme.validation,
      positiveEvidence: positiveEvidence.slice(0, 8),
      negatedEvidence: negatedEvidence.slice(0, 8),
      contradiction: positiveEvidence.length > 0 && negatedEvidence.length > 0,
    });
  }

  const sourceCounts = sources.reduce<Record<string, number>>((acc, source) => {
    acc[source.sourceType] = (acc[source.sourceType] ?? 0) + 1;
    return acc;
  }, {});
  const coveredTypes = Object.keys(sourceCounts).length;
  const textCoverageScore = Math.min(100, Math.round(coveredTypes * 18 + Math.min(28, sources.length * 2)));
  const limitations = [
    sources.length ? null : "nenhum texto clínico disponível",
    sourceCounts["sessão"] ? null : "sem narrativa/resumo de sessão",
    sourceCounts["check-in"] ? null : "sem relato textual de check-in",
    sourceCounts["dose"] ? null : "sem texto de efeito percebido ou efeito adverso por dose",
    "interpretação lexical determinística; ironia, contexto complexo e relações temporais precisam de revisão médica",
  ].filter(Boolean) as string[];

  return {
    sourcesRead: sources,
    signals: signals.sort((a, b) => {
      const rank: Record<TextSignalSeverity, number> = { urgente: 5, relevante: 4, cautela: 3, monitorar: 2, info: 1 };
      return rank[b.severity] - rank[a.severity] || b.positiveEvidence.length - a.positiveEvidence.length;
    }),
    sourceCounts,
    textCoverageScore,
    limitations,
  };
}
